import PropTypes from 'prop-types';
import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import Scrollbar from '../Scrollbar';
import { UserListHead } from '../user';
import { formatDate, TREATMENTS } from '../../utils/billing';

// The "Deployments" card used on both the team-scoped and company-wide
// part-number views. Renders the header copy, the table, and the row
// formatting rules (VRack link when active, treatment chip, dimmed
// billable-quantity when zero). The company view passes `showTeamColumn`
// so each row also shows the originating team.
DeploymentsTableCard.propTypes = {
  headLabel: PropTypes.array.isRequired,
  deployments: PropTypes.array,
  description: PropTypes.string,
  emptyMessage: PropTypes.string,
  showTeamColumn: PropTypes.bool,
  defaultOrderBy: PropTypes.string,
  defaultOrder: PropTypes.oneOf(['asc', 'desc']),
  tableMinWidth: PropTypes.number
};

DeploymentsTableCard.defaultProps = {
  deployments: [],
  description: '',
  emptyMessage: 'No deployments recorded.',
  showTeamColumn: false,
  defaultOrderBy: 'vrack',
  defaultOrder: 'asc',
  tableMinWidth: 900
};

// Map a billable quantity to the color token shown in the billable-quantity
// cell. Zero is grey (nothing to bill); anything else is green so the reader
// sees a single "this is being billed" cue without a third ambiguous state.
export function getBillableQuantityColor(billableQuantity) {
  if (billableQuantity === 0) return 'text.secondary';
  return 'success.main';
}

// Extract the comparable value for a given column id. `team` is the only
// column whose value lives at a different key (`teamName` instead of the
// matching id); everything else reads `row[id]` directly. Dates come back
// from the API as ISO strings, so lexicographic comparison gives the same
// order as a Date comparison.
function getSortValue(row, id) {
  if (id === 'team') return row.teamName;
  return row[id];
}

export default function DeploymentsTableCard({
  headLabel,
  deployments,
  description,
  emptyMessage,
  showTeamColumn,
  defaultOrderBy,
  defaultOrder,
  tableMinWidth
}) {
  // When `showTeamColumn` is true, insert a `team` entry after the first
  // column so the VRack column stays leftmost and Team sits beside it. The
  // body rows already render VRack first and Team second, so the header
  // order has to match.
  const effectiveHeadLabel = showTeamColumn
    ? [headLabel[0], { id: 'team', label: 'Team', alignRight: false }, ...headLabel.slice(1)]
    : headLabel;
  const totalColumns = effectiveHeadLabel.length;

  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [order, setOrder] = useState(defaultOrder);

  const handleRequestSort = useCallback(
    (event, property) => {
      // MUI's convention: clicking the active column flips direction;
      // clicking a different column resets to ascending.
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const sortedDeployments = useMemo(() => {
    // Sort a shallow copy so we never mutate the caller's array — the
    // parent may pass the same `deployments` reference on re-renders and
    // expect it to stay in API order.
    const copy = [...deployments];
    copy.sort((a, b) => {
      const av = getSortValue(a, orderBy);
      const bv = getSortValue(b, orderBy);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return order === 'asc' ? -1 : 1;
      if (av > bv) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [deployments, orderBy, order]);

  return (
    <Card>
      <Box sx={{ px: 3, pt: 2 }}>
        <Typography variant="h6">Deployments</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Scrollbar>
        <TableContainer sx={{ minWidth: tableMinWidth }}>
          <Table>
            <UserListHead
              order={order}
              orderBy={orderBy}
              headLabel={effectiveHeadLabel}
              rowCount={sortedDeployments.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedDeployments.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={totalColumns} sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedDeployments.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell align="left">
                      {row.treatment === TREATMENTS.BILLABLE_ACTIVE ? (
                        <Link
                          component={RouterLink}
                          to={`/racks/details/${encodeURIComponent(row.vrackId)}`}
                          underline="hover"
                          style={{ color: '#0066cc', fontWeight: 600 }}
                        >
                          {row.vrackId}
                        </Link>
                      ) : (
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {row.vrackId}
                        </Box>
                      )}
                    </TableCell>
                    {showTeamColumn && <TableCell align="left">{row.teamName}</TableCell>}
                    <TableCell align="left">
                      <strong>{row.quantity}</strong>
                    </TableCell>
                    <TableCell align="left">
                      <Box
                        component="strong"
                        sx={{
                          color: getBillableQuantityColor(row.billableQuantity)
                        }}
                      >
                        {row.billableQuantity}
                      </Box>
                    </TableCell>
                    <TableCell align="left">{row.owner}</TableCell>
                    <TableCell align="left">{formatDate(row.createdDate)}</TableCell>
                    <TableCell align="left">{formatDate(row.deletedDate)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </Card>
  );
}
